"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import { Button } from "react-bootstrap";
import { FaArrowRight, FaPlus, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";

import { parseServerFeaturesList, parseName } from "../../lib/function";

// -----------------------------------------
// COUNTRY LIST
// -----------------------------------------
const allCountries = [
  { code: "us", name: "US", slug: "usa", fullName: "USA" },
  { code: "nl", name: "NL", slug: "netherlands", fullName: "Netherlands" },
  { code: "ru", name: "RU", slug: "russia", fullName: "Russia" },
  { code: "fr", name: "FR", slug: "france", fullName: "France" },
  { code: "ca", name: "CA", slug: "canada", fullName: "Canada" },
  { code: "it", name: "IT", slug: "italy", fullName: "Italy" },
  { code: "jp", name: "JP", slug: "japan", fullName: "Japan" },
  { code: "kr", name: "KR", slug: "korea", fullName: "Korea" },
  { code: "my", name: "MY", slug: "malaysia", fullName: "Malaysia" },
  { code: "sg", name: "SG", slug: "singapore", fullName: "Singapore" },
  { code: "de", name: "DE", slug: "germany", fullName: "Germany" },
  { code: "ae", name: "AE", slug: "uae", fullName: "UAE" },
  { code: "tr", name: "TR", slug: "turkey", fullName: "Turkey" },
  { code: "gb", name: "UK", slug: "unitedkingdom", fullName: "United Kingdom" },
  { code: "bg", name: "BG", slug: "bulgaria", fullName: "Bulgaria" },
  { code: "sa", name: "SA", slug: "saudiarabia", fullName: "Saudi Arabia" },
  { code: "th", name: "TH", slug: "thailand", fullName: "Thailand" },
  { code: "cn", name: "CH", slug: "china", fullName: "China" },
  { code: "um", name: "SA", slug: "southamerica", fullName: "South America" },
  { code: "hk", name: "HK", slug: "hongkong", fullName: "Hongkong" },
];

// TYPES
type Package = {
  id: string;
  name: string;
  price: string;
  oldPrice: string;
  discount: string;
  term: string;
  features: string[];
  country: string;
  configureUrl: string;
};

type DurationPlan = {
  type: string;
  currency: string;
  period: number;
  price: number;
  actuall_price: number;
  desc: string;
  discount: number;
};

type DurationMap = {
  [key: string]: DurationPlan;
};

// -----------------------------------------
// COMPONENT START
// -----------------------------------------
const ServerPackagesDedServ = () => {
  const [packagesData, setPackagesData] = useState<Record<string, Package[]>>({});
  const [isMobile, setIsMobile] = useState(false);

  const pathname = usePathname();
  const rawSlug = pathname?.split("/").pop() || "";
  const slug = pathname?.split("-").pop();

  const links = [
    { href: rawSlug, label: "Dedicated Servers" },
    { href: "/dedicated-seo-servers", label: "Dedicated SEO Servers" },
    { href: "/dedicated-reseller", label: "Dedicated Reseller" },
    { href: "/dedicated-special-deals", label: "Dedicated Special Deals" },
  ];

  const selectedCountry =
    allCountries.find((country) => country.slug === slug) || allCountries[0];

  // Swiper ref to control slides on wheel
  const swiperRef = useRef<SwiperType | null>(null);

  // MOBILE DETECTION
  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth < 768);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  // FETCH SERVER DATA
  useEffect(() => {
    fetch("/api/plan", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rawSlug }),
    })
      .then((res) => res.json())
      .then((response) => {
        const mockData: Record<string, Package[]> = {};

        allCountries.forEach((country) => {
          mockData[country.code] = response.data.slice(0, 20).map(
            (item: { id: number; description: string; duration: DurationMap }): Package => ({
              id: item.id.toString(),
              name: parseName(item.description, "Processor"),
              price: `$${item.duration.monthly.price || 0.0}`,
              oldPrice: `$${item.duration.monthly.actuall_price.toFixed(2)}`,
              discount:
                item.duration.monthly.discount > 0
                  ? `Save ${item.duration.monthly.discount}%`
                  : "0",
              term: `Price per ${item.duration.monthly.desc}`,
              features: parseServerFeaturesList(item.description),
              country: country.code,
              configureUrl: `${process.env.NEXT_PUBLIC_API_BASE_URL}/cart.php?a=add&pid=${item.id}&billingcycle=${item.duration.monthly.type}`,
            })
          );
        });

        setPackagesData(mockData);
      });
  }, [rawSlug]);

  // Handle mouse wheel on flags area → move Swiper
  // const handleCountryWheel = (e: React.WheelEvent<HTMLDivElement>) => {
  //   if (!swiperRef.current) return;

  //   // Only react to vertical scroll
  //   if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;

  //   e.preventDefault();

  //   if (e.deltaY > 0) {
  //     swiperRef.current.slideNext();
  //   } else if (e.deltaY < 0) {
  //     swiperRef.current.slidePrev();
  //   }
  // };

  return (
    <div className="custMargin">
      <div className="container">
        {/* ------------------ PAGE HEADING ------------------ */}
        <div className="text-center my-4">
          <h1 className="display-5 fw-bold">
            Dedicated Servers Plans in{" "}
            <span className="text-capitalize">{selectedCountry.fullName}</span>
          </h1>
          <p className="fs-5 fw-normal">Backed by 24/7 On-Premise Support</p>
        </div>

        {/* ------------------ NAV LINKS ------------------ */}
        <div className="btnGroup my-4">
          {links.map(({ href, label }, index) => {
            const isActive = rawSlug === href.replace("/", "");
            return (
              <Link
                key={index}
                href={href}
                className={`mx-2 my-1 btn btn-lg ${isActive ? "darkOne" : "outline-darkZero"}`}
              >
                {label}
              </Link>
            );
          })}
        </div>

        {/* ------------------ MOBILE DROPDOWN ------------------ */}
        {isMobile && (
          <div className="dropdown w-100 mb-4">
            <h6
              className="fw-bold fs-5 mb-3 text-center d-flex justify-content-center align-items-center gap-2"
              style={{ cursor: "pointer" }}
              data-bs-toggle="modal"
              data-bs-target="#countryModal"
            >
              All Location <FaPlus className="fs-6" />
            </h6>

            <button
              className="form-select py-3 px-3 fw-semibold d-flex align-items-center justify-content-between LocCont"
              type="button"
              data-bs-toggle="dropdown"
            >
              <span className="d-flex align-items-center gap-2">
                <Image
                  src={`https://flagcdn.com/w40/${selectedCountry.code}.png`}
                  alt={selectedCountry.fullName}
                  width={25}
                  height={18}
                />
                {selectedCountry.fullName}
              </span>
              <span className="dropdown-caret"></span>
            </button>

            <ul className="dropdown-menu w-100 customFlagDropdown px-3">
              {allCountries.map((c) => (
                <li
                  key={c.slug}
                  className="dropdown-item d-flex align-items-center gap-2 py-2"
                  onClick={() =>
                    (window.location.href = `/dedicated-servers-${c.slug}`)
                  }
                >
                  <Image
                    src={`https://flagcdn.com/w40/${c.code}.png`}
                    alt={c.fullName}
                    width={25}
                    height={18}
                  />
                  <span>{c.fullName}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ----------------------------------------- */}
        {/* ----- DESKTOP FLAG CAROUSEL (SCROLL+LOOP) */}
        {/* ----------------------------------------- */}
 {/* -----------------------------------------
     DESKTOP FLAG CAROUSEL (FINAL WORKING)
------------------------------------------ */}
{!isMobile && (
  <>
    <h6
      className="fw-bold fs-5 mb-3 text-center d-flex justify-content-center align-items-center gap-2"
      style={{ cursor: "pointer" }}
      data-bs-toggle="modal"
      data-bs-target="#countryModal"
    >
      All Locations <FaPlus className="fs-6" />
    </h6>

    <div className="w-75 mx-auto mb-5">
      <div className="position-relative d-flex align-items-center">

        {/* LEFT ARROW */}
        <button
          className="country-arrow-left"
          style={{
            position: "absolute",
            left: "-15px",
            zIndex: 20,
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "#fff",
            border: "1px solid #ddd",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <FaChevronLeft />
        </button>

        {/* RIGHT ARROW */}
        <button
          className="country-arrow-right"
          style={{
            position: "absolute",
            right: "-15px",
            zIndex: 20,
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "#fff",
            border: "1px solid #ddd",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <FaChevronRight />
        </button>

        {/* SWIPER + BLOCK PAGE SCROLL */}
        <div
          id="flagWheelArea"
          style={{ width: "100%", padding: "0 40px" }}
        >
          <Swiper
            slidesPerView="auto"
            spaceBetween={20}
            modules={[Navigation]}
            navigation={{
              nextEl: ".country-arrow-right",
              prevEl: ".country-arrow-left",
            }}
            loop={true}
            simulateTouch={false}
            allowTouchMove={false}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;

              // Attach wheel listener to swiper wrapper
              const wrapper = swiper.wrapperEl;

              wrapper.addEventListener(
                "wheel",
                (e: WheelEvent) => {
                  // BLOCK PAGE SCROLL
                  e.preventDefault();

                  if (e.deltaY > 0) swiper.slideNext();
                  else swiper.slidePrev();
                },
                { passive: false }
              );
            }}
          >
            {allCountries.map((country) => {
              const isActive = country.slug === slug;

              return (
                <SwiperSlide key={country.code} style={{ width: "70px" }}>
                  <Link
                    href={`/dedicated-servers-${country.slug}`}
                    className={`text-decoration-none d-block text-center textHad py-2 countryItem ${
                      isActive ? "activeCountry" : ""
                    }`}
                  >
                    <Image
                      src={`https://flagcdn.com/w40/${country.code}.png`}
                      width={32}
                      height={22}
                      alt={country.fullName}
                    />
                    <div className="fw-bold mt-1">{country.name}</div>
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </div>
  </>
)}


        {/* ------------------ PACKAGES ------------------ */}
        <div className="row justify-content-center">
          {(packagesData[selectedCountry.code] || []).map((pkg) => (
            <div
              key={`${pkg.country}-${pkg.id}`}
              className="col-lg-3 col-md-6 col-12 mb-4"
            >
              <div className="card-body h-100">
                <h3 className="card-title fw-bold fs-5">
                  <span className="textOrange">{pkg.name}</span>
                </h3>

                <p className="text-muted">{pkg.term}</p>

                <div className="d-flex align-items-end mb-2">
                  <span className="display-6 fw-bolder">{pkg.price}</span>
                  <span className="text-muted ms-1">/mo</span>
                </div>

                {pkg.discount !== "0" && (
                  <h4 className="text-muted fw-normal my-2">
                    <del className="text-muted fs-5 fw-bold">{pkg.oldPrice}</del>
                    <span className="alert alert-warning py-1 px-2 fw-semibold ms-2 fntSize">
                      {pkg.discount}
                    </span>
                  </h4>
                )}

                <div className="text-start mb-2 mt-3">
                  <Image
                    src={`https://flagcdn.com/w40/${pkg.country}.png`}
                    alt={selectedCountry.fullName}
                    width={30}
                    height={22}
                    className="me-2"
                  />
                  <span className="fw-bold text-dark">
                    {selectedCountry.fullName}
                  </span>
                </div>

                <Link href={pkg.configureUrl} className="text-decoration-none">
                  <Button
                    className="orangeBtn fw-semibold w-100 d-block"
                    size="lg"
                  >
                    ORDER <FaArrowRight />
                  </Button>
                </Link>

                <h4 className="fs-5 fw-bold mt-3">Features</h4>
                <ul className="list-unstyled custList">
                  {pkg.features.map((feature, index) => (
                    <li key={index}>
                      <span className="text-success">✔</span>{" "}
                      <span className="textHad fw-semibold">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* ------------------ COUNTRY MODAL ------------------ */}
        <div className="modal fade" id="countryModal"  aria-hidden="true">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-3">
              <div className="modal-header border-0 d-flex justify-content-between">
                <h4 className="modal-title fw-bold">Choose your country</h4>
                <Button
  type="button"
  className="border-0 bg-transparent small float-end me-0"
  data-bs-dismiss="modal"
  aria-label="Close"
>
  <FaTimes className="textHad"/> 
</Button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  {allCountries.map((c) => (
                    <div
                      className="col-6 col-md-4 d-flex align-items-center gap-2"
                      key={c.slug}
                    >
                      <Image
                        src={`https://flagcdn.com/w40/${c.code}.png`}
                        width={28}
                        height={20}
                        alt={c.fullName}
                      />

                      <Link
                        href={`/dedicated-servers-${c.slug}`}
                        className="text-dark fw-semibold text-decoration-none"
                        onClick={() => {
                          // CLEANUP BOOTSTRAP MODAL STATE BEFORE NAVIGATION
                          const backdrops =
                            document.querySelectorAll(".modal-backdrop");
                          backdrops.forEach((b) => b.remove());

                          document.body.classList.remove("modal-open");
                          document.body.style.overflow = "";
                          document.body.style.paddingRight = "";

                          const modal = document.getElementById("countryModal");
                          if (modal) modal.classList.remove("show");
                        }}
                      >
                        {c.fullName}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ServerPackagesDedServ;
